/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * ŻÓŁTY PASEK — REGUŁY TEKSTU, TAKIE SAME JAK PO OBU STRONACH
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Pasek ma własny kanał (nie jest polem motywu):
 *   serwer   tuszama-api `MessageBarService/PasekKomunikatow.cs` — `NormalizujTresc`, limit 300
 *   klient   tuszama-mobile `c9a507c4` — `PasekKomunikatowService`, limit 300, czas pętli marquee
 *
 * ┌─ JEDNA LICZBA, DWIE JEDNOSTKI — I DLATEGO LICZYMY JAK SERWER ──────────────────────────────┐
 * │ Obie strony mają 300, ale serwer liczy `string.Length` (jednostki UTF-16 — emoji = 2),     │
 * │ a aplikacja `Array.from(tekst).length` (punkty kodowe — emoji = 1). Tekst ze 151 emoji     │
 * │ aplikacja by przyjęła, serwer odrzuci przy publikacji. Wiąże więc SERWER — panel nie może  │
 * │ pozwolić operatorowi napisać czegoś, czego nie da się opublikować. JavaScriptowe `.length` │
 * │ liczy dokładnie tak samo jak `string.Length` w C#.                                          │
 * └─────────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * Komunikaty błędów są DOSŁOWNIE te same co na serwerze — operator ma zobaczyć w panelu to samo
 * zdanie, które zobaczyłby po wysłaniu.
 */

/** `MessageBarMessage.MAKS_DLUGOSC` (serwer) = `PasekKomunikatowService.MAKS_DLUGOSC` (aplikacja). */
export const MAKS_DLUGOSC_PASKA = 300;

export const KOMUNIKAT_PUSTY = 'Treść komunikatu nie może być pusta.';
export const KOMUNIKAT_ZA_DLUGI = 'Treść komunikatu przekracza limit znaków.';
export const KOMUNIKAT_STERUJACE = 'Treść komunikatu zawiera niedozwolone znaki sterujące.';

export type WynikTresci = { ok: true; tekst: string } | { ok: false; powod: string };

/**
 * Odwzorowanie `PasekKomunikatow.NormalizujTresc` krok po kroku:
 *   1. pusty / same białe znaki → PUSTY
 *   2. `\r`, `\n`, `\t` → spacja
 *   3. każdy inny znak sterujący (kategoria Cc) → STERUJĄCE
 *   4. przycięcie końców, podwójne spacje → pojedyncze
 *   5. pusty po przycięciu → PUSTY;  dłuższy niż 300 jednostek UTF-16 → ZA DŁUGI
 */
export function normalizujTresc(surowa: string): WynikTresci {
  if (surowa.trim().length === 0) return { ok: false, powod: KOMUNIKAT_PUSTY };

  const bezLamania = surowa.replace(/[\r\n\t]/g, ' ');
  // eslint-disable-next-line no-control-regex -- właśnie te znaki są przedmiotem reguły
  if (/[\u0000-\u001F\u007F-\u009F]/.test(bezLamania)) return { ok: false, powod: KOMUNIKAT_STERUJACE };

  // Serwer zwija WYŁĄCZNIE spacje (`Replace("  ", " ")`), nie inne białe znaki — tak samo tutaj.
  const tekst = bezLamania.trim().replace(/ {2,}/g, ' ');
  if (tekst.length === 0) return { ok: false, powod: KOMUNIKAT_PUSTY };
  if (tekst.length > MAKS_DLUGOSC_PASKA) return { ok: false, powod: KOMUNIKAT_ZA_DLUGI };
  return { ok: true, tekst };
}

/** Długość tak, jak liczy SERWER (jednostki UTF-16). To ona decyduje o limicie. */
export function dlugoscWedlugSerwera(tekst: string): number {
  return tekst.length;
}

/** Długość tak, jak liczy APLIKACJA (punkty kodowe). Decyduje o tempie przewijania. */
export function dlugoscWedlugAplikacji(tekst: string): number {
  return Array.from(tekst).length;
}

/**
 * Czas jednej pętli marquee w sekundach — wzór z `menu.page.component.ts` (`c9a507c4`):
 * `Math.max(11, Math.round(8 + Array.from(tekst).length * 0.16))`.
 */
export function czasPetliPaska(tekst: string): number {
  return Math.max(11, Math.round(8 + dlugoscWedlugAplikacji(tekst) * 0.16));
}

/**
 * Czy tekst wygląda na znaczniki HTML. Serwer takiego tekstu NIE odrzuca, a aplikacja wstawia go
 * interpolacją — uczeń zobaczy `<b>` dosłownie, jako znaki. Panel ostrzega, bo operator, który
 * wpisał znacznik, najpewniej oczekiwał formatowania, którego nie będzie.
 */
export function wygladaNaHtml(tekst: string): boolean {
  return /<\s*\/?\s*[a-z!][^>]*>/i.test(tekst);
}

/**
 * Odmiana „danie" — DOSŁOWNIE jak w aplikacji, łącznie z jej uproszczeniem: 2–4 → „dania",
 * reszta → „dań" (więc 22 to „dań"). Podgląd ma pokazać to, co zobaczy uczeń, a nie poprawić
 * aplikację po cichu.
 */
export function odmianaDan(liczba: number): string {
  if (liczba === 1) return 'danie';
  if (liczba >= 2 && liczba <= 4) return 'dania';
  return 'dań';
}
